import React, { useEffect, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { FolderIconSafe as FolderIcon } from "./FolderIconSafe";
import type { SiteManifest, ManifestFolder, ManifestPageEntry } from "../types";

interface HomepageProps {
  manifest: SiteManifest;
  onNavigate: (slug: string | null) => void;
  /**
   * Folder selected via `/f/<path>`. The flat homepage only renders
   * top-level sections, so we scroll to the selected folder's top-level
   * ancestor. `null` keeps the default top-of-page position.
   */
  scrollToFolderId?: string | null;
}

function FolderSection({
  folder,
  pages,
  onNavigate,
}: {
  folder: ManifestFolder;
  pages: ManifestPageEntry[];
  onNavigate: (slug: string | null) => void;
}) {
  const folderPages = pages
    .filter((p) => p.folder_uuid === folder.uuid)
    .sort((a, b) => a.position - b.position);

  if (folderPages.length === 0) return null;

  return (
    <div id={`folder-${folder.uuid}`} className="scroll-mt-20">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
        {folder.emoji && (
          <FolderIcon emoji={folder.emoji} iconClassName="h-3.5 w-3.5" />
        )}
        {folder.name}
      </h3>
      <div className="rounded-lg border bg-card divide-y">
        {folderPages.map((page) => (
          <button
            key={page.uuid}
            onClick={() => onNavigate(page.slug)}
            className="w-full text-left px-4 py-3 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between gap-4"
          >
            <span className="text-foreground flex items-center gap-2">
              {page.emoji && (
                <FolderIcon emoji={page.emoji} iconClassName="h-3.5 w-3.5" />
              )}
              {page.title}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

export const Homepage: React.FC<HomepageProps> = ({
  manifest,
  onNavigate,
  scrollToFolderId,
}) => {
  const topLevelFolders = useMemo(
    () =>
      manifest.folders
        .filter((f) => !f.parent_folder_uuid)
        .sort((a, b) => a.position - b.position),
    [manifest.folders]
  );

  const topAncestorById = useMemo(() => {
    const byUuid = new Map<string, ManifestFolder>(
      manifest.folders.map((f) => [f.uuid, f])
    );
    const resolve = (uuid: string): string => {
      const seen = new Set<string>();
      let current = byUuid.get(uuid);
      while (current && current.parent_folder_uuid && !seen.has(current.uuid)) {
        seen.add(current.uuid);
        current = byUuid.get(current.parent_folder_uuid);
      }
      return current?.uuid ?? uuid;
    };
    return resolve;
  }, [manifest.folders]);

  useEffect(() => {
    if (!scrollToFolderId) return;
    const id = `folder-${topAncestorById(scrollToFolderId)}`;
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [scrollToFolderId, topAncestorById]);

  return (
    <div className="pb-8 help-article-content">
      <div className="space-y-6">
        {topLevelFolders.map((folder) => (
          <FolderSection
            key={folder.uuid}
            folder={folder}
            pages={manifest.pages}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
};
